import React, {
  useState,
  ReactElement,
  ChangeEvent,
  useMemo,
  useEffect,
  useImperativeHandle,
  MutableRefObject
} from 'react';
import classNames from 'classnames';

import { DropDownChevron } from 'app/scripts/components/moderation-v2/restricted-words-next/components/DropDownChevron';
import { FilterOption, Suggestion, SearchInputApi, RenderSuggestion } from '../inputTypes';
import { RestrictedWordsItemWrapper } from '../../RestrictedWordsItemWrapper';
import { useInputFloating } from '../../../hooks/useInputFloating';
import { defaultFilterOptions } from '../../../restrictedWordsUtils';
import Icon from 'utils/components/icon';

import styles from './MultiSelectionInput.module.scss';
import { OffsetOptions } from '@floating-ui/react';

const CONTAINER_OFFSET = 15;
interface MultiSelectionInput<T extends Suggestion> {
  placeholder: string;
  suggestions?: Array<T>;
  suggestionWrapperClassName?: string;
  className?: string;
  apiRef?: MutableRefObject<SearchInputApi>;
  selectedItems: Suggestion[];
  itemType: string;
  offsetConfiguration?: OffsetOptions;
  filterOption?: FilterOption<T>;
  renderSuggestion?: (item: T) => ReactElement;
  onSuggestionsVisibilityChange?: (visible: boolean) => void;
  onRemoveSelectedItems: (item: Suggestion) => void;
}

const RemoveIcon = ({ onClick }: { onClick: () => void }) => {
  return <Icon name="close" onClick={onClick} />;
};

export const MultiSelectionInput = <T extends Suggestion>(
  props: MultiSelectionInput<T>
): ReactElement => {
  const {
    suggestions,
    selectedItems,
    suggestionWrapperClassName,
    apiRef,
    placeholder,
    itemType,
    className,
    offsetConfiguration,
    filterOption = defaultFilterOptions,
    onSuggestionsVisibilityChange,
    renderSuggestion = defaultRenderSuggestion,
    onRemoveSelectedItems
  } = props;

  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const {
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    suggestionsIsOpen,
    setSuggestionsIsOpen
  } = useInputFloating({
    offsetConfiguration: offsetConfiguration || {
      mainAxis: CONTAINER_OFFSET,
      alignmentAxis: -CONTAINER_OFFSET
    }
  });

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setSuggestionsIsOpen(true);
  };

  const onArrowClick = () => {
    if (suggestionsIsOpen) {
      setSuggestionsIsOpen(false);
    } else {
      setSuggestionsIsOpen(true);
      focusInput();
    }
  };

  const clearInput = () => {
    setValue('');
  };

  const focusInput = () => {
    inputRef.current.focus();
  };

  const onFocus = () => {
    setIsFocused(true);
    setSuggestionsIsOpen(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const filteredSuggestions = useMemo(() => {
    return suggestions.filter(suggestion => filterOption(suggestion, value));
  }, [suggestions, value, filterOption]);

  useEffect(() => {
    if (onSuggestionsVisibilityChange) {
      onSuggestionsVisibilityChange(suggestionsIsOpen && filteredSuggestions.length > 0);
    }
  }, [filteredSuggestions, onSuggestionsVisibilityChange, suggestionsIsOpen]);

  useImperativeHandle(
    apiRef,
    () => {
      return {
        focusInput,
        clearInput,
        close
      };
    },
    []
  );

  const isEmptySelection = selectedItems.length === 0;
  const isSingleSelection = selectedItems.length === 1;
  const isMultipleSelection = selectedItems.length > 1;

  return (
    <div
      className={classNames(styles.inputContainer, className)}
      ref={refs.setReference}
      {...getReferenceProps()}
    >
      {isSingleSelection && (
        <div className={styles.multiSelectionContainer}>
          <div className={styles.multiSelectionItem}>
            <div className={styles.selectedItem}>{selectedItems[0].value}</div>
            <RemoveIcon onClick={() => onRemoveSelectedItems(selectedItems[0])} />
          </div>
        </div>
      )}
      {isMultipleSelection && (
        <div className={styles.multiSelectionContainer}>
          <div className={styles.multiSelectionItem}>
            <div className={styles.selectedItem}>
              {selectedItems.length} {itemType}
            </div>
            <RemoveIcon onClick={() => onRemoveSelectedItems(selectedItems[0])} />
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        placeholder={isEmptySelection ? placeholder : ''}
        onChange={onInputChange}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <div className={styles.iconContainer} onClick={clearInput}>
        {value.length > 0 && <Icon className={styles.clear} name="close" />}
        <div className={styles.dropDownChevron}>
          <DropDownChevron open={isFocused} onClick={onArrowClick} />
        </div>
      </div>
      {suggestionsIsOpen && filteredSuggestions.length > 0 && (
        <div
          className={styles.suggestionsContainer}
          style={floatingStyles}
          ref={refs.setFloating}
          {...getFloatingProps()}
        >
          {filteredSuggestions.map(suggestion => (
            <RestrictedWordsItemWrapper
              key={suggestion.value}
              className={suggestionWrapperClassName}
            >
              {renderSuggestion(suggestion)}
            </RestrictedWordsItemWrapper>
          ))}
        </div>
      )}
    </div>
  );
};

const defaultRenderSuggestion: RenderSuggestion = (item: Suggestion) => {
  return <>{item.value}</>;
};
